import { useQuery } from "@tanstack/react-query";
import api from "@/config/axiosConfig";
import { useNavigate } from "react-router-dom";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useState, useRef, useEffect } from "react";
import LazyLoad from "@tripbuilder/image/LazyLoad";
import { formatImageUrl } from "@/lib/general";
import { AvatarImage } from "@/components/ui/DefaultAvatar";
import { ShareButton } from "@/components/AddShareLike";
import { AddButton } from "@/components/AddShareLike";
import { LikeButton } from "@/components/AddShareLike";
import useTrip from "@/stores/tripBuilder/useTrip";
import { Compass } from "lucide-react";

interface CommunityTrip {
  trip_id: string;
  name: string;
  description: string;
  created_at: string;
  views: number;
  images: string[];
  tags: string[];
  creator: {
    user_id: string;
    username: string;
    first_name: string;
    last_name: string;
    profile_pic: string;
  };
  locations?: {
    name: string;
    address_components?: {
      city?: string;
      country?: string;
      country_code?: string;
    };
  }[];
}

const fetchCommunityTrips = async () => {
  const response = await api.social.get("/community-trips");
  return response.data;
};

export default function CommunityTrips() {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);
  const { copyTrip, recordEngagement } = useTrip();

  const { data: communityTrips, isLoading } = useQuery({
    queryKey: ["communityTrips"],
    queryFn: fetchCommunityTrips,
    refetchInterval: 1000 * 60 * 5,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const handleTripClick = (tripId: string) => {
    navigate(`/trip-builder/${tripId}`);
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (communityTrips && currentIndex < communityTrips.length - 2) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleCopy = async (tripId: string) => {
    try {
      await copyTrip(tripId);
    } catch (error) {
      console.error("Failed to copy trip:", error);
    }
  };

  const handleLike = async (tripId: string) => {
    try {
      await recordEngagement(tripId, "like", {
        trip_id: tripId,
      });
    } catch (error) {
      console.error("Failed to like trip:", error);
    }
  };

  useEffect(() => {
    if (carouselRef.current && communityTrips?.length > 0) {
      const scrollAmount = currentIndex * (carouselRef.current.offsetWidth / 3);
      carouselRef.current.scrollTo({
        left: scrollAmount,
        behavior: "smooth",
      });
    }
  }, [currentIndex, communityTrips]);

  if (isLoading) {
    // Create a mock array to simulate the same number of visible cards as in the loaded state
    const visibleCards = [...Array(2)].map((_, i) => i);

    return (
      <div className="flex flex-col space-y-4 w-full flex-1 h-full overflow-hidden">
        <div className="flex items-center justify-between">
          <div className="bg-indigo-800 text-white flex flex-row items-center space-x-2 px-3 py-2 text-sm font-medium rounded-full transition ease-in-out duration-300">
            <i className="fa-regular fa-compass fa-lg" />
            <h2>Community Trips</h2>
          </div>
          <div className="flex space-x-2">
            <button className="p-2 rounded-full bg-neutral-200 text-neutral-400 cursor-default">
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
            <button className="p-2 rounded-full bg-neutral-200 text-neutral-400 cursor-default">
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div
          className="grid grid-cols-2 gap-4 overflow-auto flex-1 pb-0.5 auto-rows-fr"
          style={{ minHeight: "0" }}
        >
          {visibleCards.map((i) => (
            <div
              key={i}
              className="relative w-full rounded-xl overflow-hidden flex-shrink-0 transition-all duration-300 bg-white border border-neutral-200 h-full"
              style={{ minHeight: "250px" }}
            >
              <div className="h-full flex flex-col">
                <div className="relative" style={{ height: "70%" }}>
                  <div className="w-full h-full overflow-hidden">
                    <div className="w-full h-full bg-neutral-200"></div>
                  </div>
                </div>
                <div
                  className="px-3 pt-3 pb-2.5 flex flex-col justify-around"
                  style={{ height: "30%" }}
                >
                  <div className="flex justify-between items-start">
                    <div className="h-5 w-3/4 bg-neutral-200 rounded-md"></div>
                  </div>

                  <div className="flex items-center justify-between">
                    {/* User Info Skeleton */}
                    <div className="flex items-center space-x-2 backdrop-blur-sm py-2 rounded-full">
                      <div className="w-5 h-5 rounded-full bg-neutral-200"></div>
                      <div className="h-3 w-20 bg-neutral-200 rounded-full"></div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <div className="w-5 h-5 rounded-full bg-neutral-200"></div>
                      <div className="w-5 h-5 rounded-full bg-neutral-200"></div>
                      <div className="w-5 h-5 rounded-full bg-neutral-200"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!communityTrips || communityTrips.length === 0) {
    return (
      <div className="flex flex-col space-y-4 w-full flex-1 h-full overflow-hidden">
        <div className="flex items-center justify-between">
          <div className="bg-indigo-800 text-white flex flex-row items-center space-x-2 px-3 py-2 text-sm font-medium rounded-full transition ease-in-out duration-300">
            <i className="fa-regular fa-compass fa-lg" />
            <h2>Community Trips</h2>
          </div>
          <div className="flex space-x-2">
            <button className="p-2 rounded-full bg-neutral-200 text-neutral-400 cursor-default">
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
            <button className="p-2 rounded-full bg-neutral-200 text-neutral-400 cursor-default">
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center h-full">
          <div className="bg-neutral-50 rounded-full p-4 mb-4">
            <Compass className="w-8 h-8 text-neutral-500" strokeWidth={1.5} />
          </div>
          <h3 className="text-lg font-medium text-neutral-900 mb-2">
            No community trips found
          </h3>
          <p className="text-neutral-500 text-center text-sm max-w-xs font-light">
            Check back later for community adventures!
          </p>
        </div>
      </div>
    );
  }

  const colours = ["indigo", "blue", "green", "yellow", "red"];

  return (
    <div className="flex flex-col space-y-4 w-full flex-1 h-full overflow-hidden">
      <div className="flex items-center justify-between pr-1">
        <div className="bg-indigo-800 text-white flex flex-row items-center space-x-2 px-3 py-2 text-sm font-medium rounded-full transition ease-in-out duration-300">
          <i className="fa-regular fa-compass fa-lg" />
          <h2>Community Trips</h2>
        </div>
        <div className="flex space-x-2">
          <button
            className={`flex items-center justify-center w-8 h-8 rounded-full transition-all ring-1 ${
              currentIndex > 0
                ? "bg-white text-indigo-600 ring-indigo-200 hover:bg-indigo-50"
                : "bg-neutral-200 text-neutral-400 opacity-75 ring-neutral-200"
            }`}
            onClick={handlePrevious}
          >
            <ChevronLeftIcon className="w-4 h-4" strokeWidth={2.5} />
          </button>
          <button
            className={`flex items-center justify-center w-8 h-8 rounded-full transition-all ring-1 ${
              communityTrips && currentIndex < communityTrips.length - 2
                ? "bg-white text-indigo-600 ring-indigo-200 hover:bg-indigo-50"
                : "bg-neutral-200 text-neutral-400 opacity-75 ring-neutral-200"
            }`}
            onClick={handleNext}
          >
            <ChevronRightIcon className="w-4 h-4" strokeWidth={2.5} />
          </button>
        </div>
      </div>

      <div
        ref={carouselRef}
        className="grid grid-cols-2 gap-4 overflow-auto flex-1 pb-2 auto-rows-fr"
      >
        {communityTrips.map((trip: CommunityTrip, index: number) => {
          const randomColour =
            colours[Math.floor(Math.random() * colours.length)];

          return (
            <div
              key={trip.trip_id}
              className={`relative w-full rounded-xl overflow-hidden flex-shrink-0 cursor-pointer transition-all duration-300 bg-white border border-neutral-200 hover:border-neutral-300 hover:shadow-sm h-full ${
                index < currentIndex || index >= currentIndex + 2
                  ? "hidden"
                  : ""
              }`}
              onClick={() => handleTripClick(trip.trip_id)}
              style={{ minHeight: "250px" }}
            >
              <div className="h-full flex flex-col">
                <div className="relative" style={{ height: "70%" }}>
                  <div className="w-full h-full overflow-hidden">
                    <div className="w-full h-full">
                      {trip.images?.[0] ? (
                        <LazyLoad
                          src={formatImageUrl(trip.images[0], "high")}
                          lowResSrc={formatImageUrl(trip.images[0], "low")}
                          alt={trip.name}
                          className="w-full h-full object-cover transition duration-700 ease-in-out group-hover:scale-105"
                          objectFit="cover"
                        />
                      ) : (
                        <div
                          className={`w-full h-full bg-gradient-to-br from-${randomColour}-400 to-${randomColour}-600 flex items-center justify-center`}
                        >
                          <span className="text-white text-3xl font-semibold">
                            {trip.name?.[0]?.toUpperCase() || "T"}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div
                  className="px-3 pt-3 pb-2.5 flex flex-col justify-around"
                  style={{ height: "30%" }}
                >
                  <div className="flex justify-between items-start">
                    <h3 className="font-semibold text-base text-neutral-800 line-clamp-1 flex-1">
                      {trip.name || "Unnamed Trip"}
                    </h3>
                  </div>

                  <div className="flex items-center justify-between">
                    {/* User Info */}
                    <div className="flex items-center space-x-2 backdrop-blur-sm py-1 rounded-full">
                      <AvatarImage
                        src={trip.creator.profile_pic || ""}
                        firstName={trip.creator.first_name || ""}
                        lastName={trip.creator.last_name || ""}
                        className="w-5 h-5 rounded-full"
                        alt={trip.creator.username || ""}
                      />
                      <span className="text-xs font-light text-neutral-800">
                        {trip.creator.first_name} {trip.creator.last_name}
                      </span>
                    </div>

                    <div
                      className="flex items-center space-x-4"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <AddButton
                        tripName={trip.name || "Unnamed Trip"}
                        onAdd={() => handleCopy(trip.trip_id)}
                        compact={true}
                      />
                      <ShareButton
                        count={0}
                        tripId={trip.trip_id}
                        tripName={trip.name || "Unnamed Trip"}
                        tripCreator={trip.creator.username || "Anonymous"}
                        isModalOpen={isModalOpen}
                        setIsModalOpen={setIsModalOpen}
                        showCount={false}
                        className="group text-neutral-600 hover:text-neutral-800"
                      />
                      <LikeButton
                        count={0}
                        iconClassName="fa-lg"
                        userLiked={false}
                        isLiked={false}
                        className="group text-neutral-600 hover:text-neutral-800"
                        onLike={() => handleLike(trip.trip_id)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
